import React, { FunctionComponent, useMemo, useEffect, useState } from "react"
import { Button } from "@citydna/common"
import {
  makeStyles,
  Theme,
  Divider,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@material-ui/core"
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from "@material-ui/icons"
import { useForm, FormProvider } from "react-hook-form"
import { FeatureFields } from "./FeatureFields"
import {
  sanitiseForEsri,
  parseAttributesForForm,
  parseEsriFields,
} from "./fieldHelpers"
import { useSnackbar } from "notistack"
import { v4 as uuid } from "uuid"
import { Storage } from "aws-amplify"
import { NEW_GRAPHIC_OBJECTID } from "../feature-layer/AddFeatureButton"

/** Scoped @material-ui/core stylesheet */
const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  textField: {
    marginBottom: theme.spacing(2),
  },
  form: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },
  accordion: {
    boxShadow: "none",
  },
  accordionHeading: {
    ...theme.typography.button,
    textTransform: "none",
    color:
      theme.palette.type === "dark"
        ? theme.palette.grey[50]
        : theme.palette.grey[800],
  },
  accordionDetails: {
    flexDirection: "column",
  },
  deleteButton: {
    marginRight: "auto",
    color: theme.palette.warning.main,
  },
  saveButton: {
    marginRight: theme.spacing(1),
  },
}))

type applyEditResult = {
  objectId?: number
  uniqueId?: number
  globalId?: number | null
  success: boolean
}

export type applyEditsResult = {
  addResults: applyEditResult[]
  deleteResults: applyEditResult[]
  updateResults: applyEditResult[]
}

interface FeatureFormProps {
  featureLayer?: __esri.FeatureLayer
  feature?: __esri.Graphic
  onCancel?: () => void
  beforeSave?: () => void
  onSuccess?: (result: applyEditsResult) => void
  onDelete?: (result: applyEditsResult) => void
}

export const FeatureForm: FunctionComponent<FeatureFormProps> = ({
  featureLayer,
  feature,
  onCancel,
  beforeSave,
  onSuccess,
  onDelete,
}) => {
  /** Get scoped CSS classes */
  const classes = useStyles()

  /** Parse the layer fields to augmented schema fields */
  const schemaFields = useMemo(
    () => featureLayer && parseEsriFields(featureLayer.fields),
    [featureLayer]
  )

  /** Split the fields between DNA specific ones and others */
  const [dnaFields, otherFields] = useMemo(() => {
    return [
      schemaFields?.filter((field) => field.name?.includes("dna_")),
      schemaFields?.filter((field) => !field.name?.includes("dna_")),
    ]
  }, [schemaFields])

  /** Setup the form */
  const attributes = useMemo(() => feature?.toJSON().attributes, [feature])
  const methods = useForm<typeof attributes>()

  /** Set the default values when they become available */
  const { reset } = methods
  useEffect(() => {
    if (schemaFields && attributes) {
      reset(parseAttributesForForm(attributes, schemaFields))
    }
  }, [reset, attributes, schemaFields])

  /** Handle the form submission - note need to handle adding features */
  const { enqueueSnackbar } = useSnackbar()
  const handleSubmit = async (values: typeof attributes) => {
    try {
      if (feature && featureLayer) {
        const attributes: __esri.Graphic["attributes"] = sanitiseForEsri(
          values,
          schemaFields
        )

        /** Send uploads to s3 */
        const uploads = await Promise.all<[keyof typeof attributes, string][]>(
          Object.entries<File>(attributes)
            .filter(([, value]) => value instanceof File)
            .map(([fieldName, file]: [string, File]) => {
              const [name, suffix] = values[fieldName].name.split(".")
              return Storage.put(name + "-" + uuid() + "." + suffix, file, {
                acl: "public-read",
                contentType: file.type,
                // @ts-ignore - Storage.put doesn't return the right object type
              }).then((result) => [fieldName, result.key])
            })
        )

        /** Re-assign the uploads */
        uploads.forEach(([fieldName, key]) => {
          // @ts-ignore - Can't index attributes
          attributes[fieldName] = "s3:" + key
        })

        // Set the new attributes against the feature
        feature.attributes = attributes

        // call beforeSave if it's there
        beforeSave && beforeSave()

        // apply the edits
        const result =
          attributes.OBJECTID === NEW_GRAPHIC_OBJECTID
            ? await featureLayer.applyEdits({ addFeatures: [feature] })
            : await featureLayer.applyEdits({ updateFeatures: [feature] })

        // let the user know it's saved!
        enqueueSnackbar("Feature saved.", { variant: "success" })
        onSuccess && onSuccess(result)
      }
    } catch (error) {
      console.log(JSON.stringify(error))
      enqueueSnackbar("Couldn't save the feature", { variant: "error" })
    }
  }

  /** Handle deleting a feature */
  const [sure, setSure] = useState(false)
  const handleDelete = () => {
    if (sure) {
      featureLayer
        ?.applyEdits({ deleteFeatures: [feature] })
        .then((results) => onDelete && onDelete(results))
        .then(() => enqueueSnackbar("Feature deleted"))
        .then(() => onCancel && onCancel())
        .catch(() =>
          enqueueSnackbar("There was an error deleting the feature", {
            variant: "error",
          })
        )
    } else {
      setSure(true)
    }
  }

  return (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
      flexGrow={1}
      component="form"
      onSubmit={methods.handleSubmit(handleSubmit)}
    >
      <Box flexGrow={1} overflow="scroll" p={2}>
        <FormProvider {...methods}>
          <FeatureFields fields={dnaFields} />
          <Accordion className={classes.accordion}>
            <AccordionSummary
              expandIcon={<ExpandMoreRoundedIcon />}
              className={classes.accordionHeading}
            >
              <Typography className={classes.accordionHeading}>
                Other fields
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <FeatureFields fields={otherFields} />
            </AccordionDetails>
          </Accordion>
        </FormProvider>
      </Box>
      <Divider />
      <Box p={2} display="flex" justifyContent="flex-end">
        {feature?.attributes.OBJECTID !== NEW_GRAPHIC_OBJECTID && (
          <Button className={classes.deleteButton} onClick={handleDelete}>
            {sure ? "Are you sure?" : "Delete feature"}
          </Button>
        )}
        <Button
          size="small"
          type="submit"
          variant="contained"
          color="secondary"
          // loading={}
          className={classes.saveButton}
        >
          Save
        </Button>
        <Button
          size="small"
          onClick={onCancel}
          // disabled={isSubmitting}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  )
}
